// File d'envoi hors connexion (carnet de chantier, incrément 6 — conception § 7.2 à 7.4).
//
// Chaque saisie de terrain (coche, annulation, temps passé, consigne, réserve et levée, mesure, pièce posée, photo) devient une
// OPÉRATION rangée dans IndexedDB AVANT tout envoi : identifiant d'envoi (clientOpId) généré sur le téléphone, heure du téléphone
// (occurredAt). Le serveur rejoue sans doublon (index unique sur clientOpId) et répond opération par opération.
//
// Règles :
//  - CLOISONNÉE par utilisateur ET par organisation : chaque opération porte son « propriétaire » (organisation:utilisateur) ; un
//    autre compte sur le même téléphone ne la voit pas et ne l'envoie jamais (elle partirait sous son nom) ;
//  - rien n'est perdu en silence : une opération refusée reste sur le téléphone (« Envois refusés ») avec son motif, jusqu'à ce que
//    la personne la corrige, la renvoie ou l'abandonne ;
//  - ordre de la saisie conservé ; photos envoyées APRÈS les autres opérations, une par une (sauf la photo d'une étape « photo
//    demandée », envoyée juste avant sa coche : le serveur exige la photo ou un motif) ; un envoi coupé reprend au suivant ;
//  - aucun jeton ni secret ici : l'envoi utilise le cookie de session (inaccessible au script) ; seules les saisies sont gardées ;
//  - effacée à la déconnexion une fois vide (la déconnexion est bloquée tant qu'elle ne l'est pas, voir app/deconnexion.tsx).
//
// Bons d'intervention (31/10/2026, migration 047) : un enregistrement fait sans réseau devient une opération « bon » (le corps complet
// de PATCH /api/bons/:id, la version de base lue — majLe — ou l'envoi précédent du même bon — apresOp —, et le formulaire pour
// l'affichage). Rejouées une par une, dans l'ordre de saisie ; le serveur ne refait rien pour un identifiant déjà reçu et refuse
// (409, rien n'est écrasé) si un collègue a modifié le bon entre-temps. Photos, sauvegardes et signature d'un bon : réseau exigé.
import { useEffect, useState } from 'react';
import { useSession, type Moi } from './session.tsx';

export type TypeOperation = 'coche' | 'annulation' | 'temps' | 'releve' | 'reserve' | 'levee' | 'mesure' | 'piece' | 'photo' | 'bon';
export type EtatOperation = 'en_attente' | 'refusee';

export interface OperationFile {
  clientOpId: string;
  /** « organisation:utilisateur » : la file d'un compte n'est jamais lue ni envoyée par un autre. */
  proprio: string;
  chantierId: string;
  chantierLibelle: string;
  type: TypeOperation;
  /** Corps de la saisie, tel que la route en ligne l'attend (sans clientOpId ni occurredAt, portés à part). */
  donnees: Record<string, unknown>;
  /** Heure du téléphone au moment de la saisie (ISO 8601). */
  occurredAt: string;
  /** Ordre de saisie. */
  seq: number;
  etat: EtatOperation;
  /** Ce que c'est, en clair (écran « Envois »). */
  libelle: string;
  /** Photo réduite (JPEG), pour les opérations « photo ». */
  photo?: Blob;
  /** Photo d'une étape « photo demandée » : envoyée juste AVANT les opérations qui la suivent. */
  prealable?: boolean;
  /** Pour l'affichage immédiat dans « Ma journée » (étape cochée, en attente d'envoi). */
  etapeId?: string | null;
  /** Opération « bon » : le bon visé et le formulaire tel qu'enregistré (affiché par la fiche tant qu'il n'est pas parti). */
  bon?: { id: string; reference: string; formulaire: unknown };
  tentatives: number;
  dernierEssai?: number;
  refus?: { http?: number; code?: string; message: string; le: number };
}

export type NouvelleOperation = Omit<OperationFile, 'proprio' | 'seq' | 'etat' | 'tentatives' | 'clientOpId' | 'occurredAt'> & {
  clientOpId?: string; occurredAt?: string;
};

export interface Verdict {
  clientOpId: string | null; type: string | null;
  statut: 'acceptee' | 'deja_recue' | 'sans_effet' | 'refusee' | 'a_reessayer';
  id?: string | null; http?: number; code?: string; message?: string; heure_incoherente?: boolean; avertissements?: string[];
  equipement?: { id: string; label: string; cree: boolean; complete: string[] }; remplace?: { id: string; label: string } | null;
}

export const proprioDe = (moi: Pick<Moi, 'organisation' | 'utilisateur'>) => `${moi.organisation.id}:${moi.utilisateur.id}`;

/** Identifiant d'envoi. crypto.randomUUID n'existe qu'en contexte sécurisé (https) : sur le Wi-Fi local (http), on le construit. */
export function nouvelIdentifiant(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const o = crypto.getRandomValues(new Uint8Array(16));
  o[6] = (o[6] & 0x0f) | 0x40; o[8] = (o[8] & 0x3f) | 0x80;
  const h = [...o].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// ═══ IndexedDB ═══════════════════════════════════════════════════════════════════════════════════════
export const BASE_FILE = 'trame-envois';
const MAGASIN = 'operations';
const EVT = 'trame:file-envoi';
const canal = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('trame-envois') : null;

export const fileDisponible = () => typeof indexedDB !== 'undefined';

let ouverture: Promise<IDBDatabase> | null = null;
function ouvrir(): Promise<IDBDatabase> {
  if (!fileDisponible()) return Promise.reject(new Error('Ce navigateur ne permet pas de garder des saisies sans réseau.'));
  if (ouverture) return ouverture;
  const creer = (b: IDBDatabase) => {
    if (b.objectStoreNames.contains(MAGASIN)) return;
    b.createObjectStore(MAGASIN, { keyPath: 'clientOpId' }).createIndex('proprio', 'proprio');
  };
  ouverture = new Promise<IDBDatabase>((ok, ko) => {
    const r = indexedDB.open(BASE_FILE);
    r.onupgradeneeded = () => creer(r.result);
    r.onsuccess = () => {
      let base = r.result;
      // Base présente mais sans magasin (créée vide par un autre outil) : on la complète par une montée de version.
      if (!base.objectStoreNames.contains(MAGASIN)) {
        const v = base.version + 1;
        base.close();
        const r2 = indexedDB.open(BASE_FILE, v);
        r2.onupgradeneeded = () => creer(r2.result);
        r2.onsuccess = () => { base = r2.result; base.onversionchange = () => { base.close(); ouverture = null; }; ok(base); };
        r2.onerror = () => { ouverture = null; ko(r2.error ?? new Error('Stockage du téléphone indisponible.')); };
        return;
      }
      // Base supprimée par un autre onglet (déconnexion) : on la rouvrira à la demande.
      base.onversionchange = () => { base.close(); ouverture = null; };
      base.onclose = () => { ouverture = null; };
      ok(base);
    };
    r.onerror = () => { ouverture = null; ko(r.error ?? new Error('Stockage du téléphone indisponible.')); };
    r.onblocked = () => { /* un autre onglet ferme la base : l'ouverture reprend seule */ };
  });
  return ouverture;
}

async function transaction<T>(mode: IDBTransactionMode, f: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T> {
  const base = await ouvrir();
  return new Promise<T>((ok, ko) => {
    const t = base.transaction(MAGASIN, mode);
    const req = f(t.objectStore(MAGASIN));
    t.oncomplete = () => ok(req ? req.result : (undefined as T));
    t.onerror = () => ko(t.error ?? new Error('Écriture impossible dans le stockage du téléphone.'));
    t.onabort = () => ko(t.error ?? new Error('Écriture interrompue dans le stockage du téléphone.'));
  });
}

function signaler() {
  window.dispatchEvent(new Event(EVT));
  try { canal?.postMessage('maj'); } catch { /* canal fermé */ }
}

/** Opérations de ce propriétaire, dans l'ordre de saisie. */
export async function lister(proprio: string): Promise<OperationFile[]> {
  if (!fileDisponible() || effacee) return [];
  // Lire ne crée jamais la base : absente (rien n'a jamais été saisi, ou effacée à la déconnexion), la file est vide.
  if (!ouverture && typeof indexedDB.databases === 'function') {
    const bases = await indexedDB.databases().catch(() => null);
    if (bases && !bases.some((b) => b.name === BASE_FILE)) return [];
  }
  const l = await transaction<OperationFile[]>('readonly', (s) => s.index('proprio').getAll(proprio));
  return (l ?? []).sort((a, b) => a.seq - b.seq);
}

let dernierSeq = 0;
const prochainSeq = () => { dernierSeq = Math.max(Date.now() * 1000, dernierSeq + 1); return dernierSeq; };

async function ecrire(ops: OperationFile[]) {
  if (!ops.length) return;
  await transaction('readwrite', (s) => { for (const o of ops) s.put(o); });
  signaler();
}

async function supprimer(ids: string[]) {
  if (!ids.length) return;
  await transaction('readwrite', (s) => { for (const id of ids) s.delete(id); });
  signaler();
}

/** Range des saisies dans la file (durable AVANT tout envoi). */
export async function ajouter(proprio: string, nouvelles: NouvelleOperation[]): Promise<OperationFile[]> {
  effacee = false;
  const maintenant = new Date().toISOString();
  const ops: OperationFile[] = nouvelles.map((n) => ({
    ...n, clientOpId: n.clientOpId ?? nouvelIdentifiant(), occurredAt: n.occurredAt ?? maintenant, proprio, seq: prochainSeq(), etat: 'en_attente', tentatives: 0,
  }));
  await ecrire(ops);
  return ops;
}

/** Saisies qui dépendent d'une autre (annulation d'une coche, levée ou photo d'une réserve…, désignée par son identifiant d'envoi). */
const dependDe = (o: OperationFile, parent: string) =>
  ['cocheOp', 'reserveOp', 'pieceOp', 'mesureOp', 'apresOp'].some((k) => o.donnees[k] === parent);

/** Enregistrements d'un bon gardés sur le téléphone (ordre de saisie). */
export const opsDuBon = (ops: OperationFile[], bonId: string) => ops.filter((o) => o.type === 'bon' && o.bon?.id === bonId);

/**
 * Range un enregistrement de bon dans la file : il s'appuie sur l'enregistrement précédent du même bon encore sur le téléphone
 * (apresOp), sinon sur la version du bon lue par l'appareil (majLe). Même identifiant d'envoi qu'un essai en ligne interrompu : un
 * enregistrement arrivé malgré la coupure ne sera pas appliqué deux fois.
 */
export async function rangerBon(proprio: string, o: { bonId: string; reference: string; corps: Record<string, unknown>; majLe: string;
  formulaire: unknown; clientOpId?: string }): Promise<OperationFile> {
  const precedent = opsDuBon(await lister(proprio), o.bonId).at(-1);
  const { majLe: _ignore, ...corps } = o.corps;
  const [op] = await ajouter(proprio, [{
    clientOpId: o.clientOpId, type: 'bon', chantierId: '', chantierLibelle: `Bon ${o.reference}`, libelle: `Enregistrement du bon ${o.reference}`,
    donnees: { ...corps, ...(precedent ? { apresOp: precedent.clientOpId } : { majLe: o.majLe }) },
    bon: { id: o.bonId, reference: o.reference, formulaire: o.formulaire },
  }]);
  return op;
}

/** Abandonner une saisie (et ce qui en dépend) : effacée du téléphone sans être envoyée. */
export async function abandonner(proprio: string, clientOpId: string) {
  const l = await lister(proprio);
  await supprimer(l.filter((o) => o.clientOpId === clientOpId || dependDe(o, clientOpId)).map((o) => o.clientOpId));
}

/** Remettre en attente une saisie refusée (telle quelle, ou corrigée), et ce qui en dépend et avait été refusé avec elle. */
export async function remettreEnAttente(proprio: string, clientOpId: string, donnees?: Record<string, unknown>) {
  const l = await lister(proprio);
  const op = l.find((o) => o.clientOpId === clientOpId);
  if (!op) return;
  // Identifiant déjà servi pour une autre saisie (cas rarissime) : la saisie corrigée repart sous un nouvel identifiant.
  const nouvelId = op.refus?.http === 409 && /identifiant d’envoi/.test(op.refus.message) ? nouvelIdentifiant() : op.clientOpId;
  const maj: OperationFile = { ...op, clientOpId: nouvelId, donnees: donnees ?? op.donnees, etat: 'en_attente', refus: undefined, tentatives: 0 };
  const dependantes = l.filter((o) => o.etat === 'refusee' && dependDe(o, clientOpId)).map((o) => ({
    ...o, etat: 'en_attente' as const, refus: undefined, tentatives: 0,
    donnees: Object.fromEntries(Object.entries(o.donnees).map(([k, v]) => [k, v === clientOpId ? nouvelId : v])),
  }));
  if (nouvelId !== clientOpId) await supprimer([clientOpId]);
  await ecrire([maj, ...dependantes]);
}

/** À la déconnexion (file vide, ou effacement confirmé) : plus rien de ce compte sur le téléphone ; la base disparaît si elle est vide. */
export async function viderFile(proprio: string) {
  if (!fileDisponible()) return;
  const l = await lister(proprio).catch(() => [] as OperationFile[]);
  await supprimer(l.map((o) => o.clientOpId)).catch(() => {});
  const reste = await transaction<number>('readonly', (s) => s.count()).catch(() => 1);
  if (reste === 0) {
    // Plus aucune lecture ne rouvre la base pendant son effacement (elle serait recréée vide) ; la prochaine saisie la recrée.
    effacee = true;
    (await ouvrir().catch(() => null))?.close();
    ouverture = null;
    await new Promise<void>((ok) => {
      const r = indexedDB.deleteDatabase(BASE_FILE);
      r.onsuccess = r.onerror = () => ok();
      setTimeout(ok, 3000);   // un autre onglet tarde à fermer la base : l'effacement se termine dès qu'il la ferme
    });
  }
  signaler();
}

/** Base effacée à la déconnexion : les lectures rendent une file vide sans la rouvrir, jusqu'à la prochaine saisie. */
let effacee = false;

// ═══ Envoi ═══════════════════════════════════════════════════════════════════════════════════════════
export const TAILLE_LOT = 50;
const DELAI_LOT_MS = 25_000;
const DELAI_PHOTO_MS = 90_000;

export interface Bilan {
  envoyees: number; refusees: number;
  /** Arrêt avant la fin : réseau absent, session à rouvrir, ou serveur indisponible (tout reste dans la file). */
  arret: null | 'reseau' | 'session' | 'serveur';
  avertissements: string[];
}

/** Verdicts reçus pendant cette ouverture de l'application (le volet de saisie y lit le sien). */
const verdicts = new Map<string, Verdict>();
export const verdictDe = (clientOpId: string) => verdicts.get(clientOpId);

class Arret extends Error { constructor(public raison: 'reseau' | 'session' | 'serveur') { super(raison); } }

async function appeler(url: string, init: RequestInit, delai: number): Promise<Response> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), delai);
  try {
    const r = await fetch(url, { ...init, credentials: 'same-origin', signal: ctrl.signal });
    if (r.status === 401) throw new Arret('session');
    // Réponse « hors connexion » du service de l'application (réseau tombé pendant l'envoi).
    if (r.status === 503 && (r.headers.get('content-type') ?? '').includes('json')) {
      const c = await r.clone().json().catch(() => null);
      if (c?.erreur?.code === 'hors_connexion') throw new Arret('reseau');
    }
    return r;
  } catch (e) {
    if (e instanceof Arret) throw e;
    throw new Arret('reseau');
  } finally { clearTimeout(t); }
}

const refus = (o: OperationFile, message: string, http?: number, code?: string): OperationFile =>
  ({ ...o, etat: 'refusee', refus: { message, http, code, le: Date.now() } });

async function noterEssai(ops: OperationFile[]) {
  await ecrire(ops.map((o) => ({ ...o, tentatives: o.tentatives + 1, dernierEssai: Date.now() })));
}

async function envoyerLot(chantierId: string, ops: OperationFile[], bilan: Bilan) {
  let r: Response;
  try {
    r = await appeler(`/api/chantiers/${chantierId}/envois`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Trame': '1' },
      body: JSON.stringify({ operations: ops.map((o) => ({ clientOpId: o.clientOpId, type: o.type, occurredAt: o.occurredAt, donnees: o.donnees })) }),
    }, DELAI_LOT_MS);
  } catch (e) { await noterEssai(ops); throw e; }
  const corps = await r.json().catch(() => null) as { resultats?: Verdict[]; erreur?: { message?: string; code?: string } } | null;
  if (!r.ok) {
    // Le lot entier est refusé : chantier introuvable ou accès retiré (404), dossier classé (409), lot invalide (400)…
    if (r.status >= 400 && r.status < 500) {
      const message = corps?.erreur?.message ?? `Envoi refusé (${r.status}).`;
      await ecrire(ops.map((o) => refus(o, message, r.status, corps?.erreur?.code)));
      bilan.refusees += ops.length;
      return;
    }
    await noterEssai(ops);
    throw new Arret('serveur');
  }
  const resultats = corps?.resultats ?? [];
  const aSupprimer: string[] = []; const aEcrire: OperationFile[] = [];
  let panne = false;
  ops.forEach((o, i) => {
    const v = resultats[i];
    if (!v) { aEcrire.push({ ...o, tentatives: o.tentatives + 1, dernierEssai: Date.now() }); panne = true; return; }
    verdicts.set(o.clientOpId, v);
    if (v.statut === 'acceptee' || v.statut === 'deja_recue' || v.statut === 'sans_effet') {
      aSupprimer.push(o.clientOpId); bilan.envoyees++;
      bilan.avertissements.push(...(v.avertissements ?? []), ...(v.statut === 'sans_effet' && v.message ? [v.message] : []));
    } else if (v.statut === 'refusee') {
      aEcrire.push(refus(o, v.message ?? 'Refusée par le serveur.', v.http, v.code)); bilan.refusees++;
    } else {
      aEcrire.push({ ...o, tentatives: o.tentatives + 1, dernierEssai: Date.now() }); panne = true;
    }
  });
  await supprimer(aSupprimer);
  await ecrire(aEcrire);
  if (panne) throw new Arret('serveur');
}

async function envoyerPhoto(o: OperationFile, bilan: Bilan) {
  if (!o.photo) { await ecrire([refus(o, 'La photo n’a pas été gardée sur ce téléphone : reprenez-la.')]); bilan.refusees++; return; }
  const fd = new FormData();
  for (const [k, v] of Object.entries(o.donnees)) if (v !== null && v !== undefined && v !== '') fd.append(k, String(v));
  fd.append('clientOpId', o.clientOpId);
  fd.append('occurredAt', o.occurredAt);
  fd.append('photo', o.photo, 'photo.jpg');
  let r: Response;
  try { r = await appeler(`/api/chantiers/${o.chantierId}/photos`, { method: 'POST', headers: { 'X-Trame': '1' }, body: fd }, DELAI_PHOTO_MS); }
  catch (e) { await noterEssai([o]); throw e; }
  const corps = await r.json().catch(() => null) as ({ id?: string; erreur?: { message?: string; code?: string }; avertissements?: string[] } | null);
  if (r.ok) {
    verdicts.set(o.clientOpId, { clientOpId: o.clientOpId, type: 'photo', statut: 'acceptee', id: corps?.id ?? null, avertissements: corps?.avertissements ?? [] });
    await supprimer([o.clientOpId]); bilan.envoyees++;
    return;
  }
  if (r.status >= 400 && r.status < 500) {
    await ecrire([refus(o, corps?.erreur?.message ?? `Photo refusée (${r.status}).`, r.status, corps?.erreur?.code)]); bilan.refusees++;
    return;
  }
  await noterEssai([o]);
  throw new Arret('serveur');
}

/** Enregistrement d'un bon fait sans réseau : rejoué par la route en ligne (PATCH), avec son identifiant d'envoi. */
async function envoyerBon(o: OperationFile, bilan: Bilan) {
  if (!o.bon?.id) { await ecrire([refus(o, 'Bon d’intervention inconnu : enregistrement illisible.')]); bilan.refusees++; return; }
  let r: Response;
  try {
    r = await appeler(`/api/bons/${encodeURIComponent(o.bon.id)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', 'X-Trame': '1' },
      body: JSON.stringify({ ...o.donnees, clientOpId: o.clientOpId, occurredAt: o.occurredAt }),
    }, DELAI_LOT_MS);
  } catch (e) { await noterEssai([o]); throw e; }
  const corps = await r.json().catch(() => null) as ({ envoi?: { statut?: string }; maj_le?: string; erreur?: { message?: string; code?: string } } | null);
  if (r.ok) {
    verdicts.set(o.clientOpId, { clientOpId: o.clientOpId, type: 'bon', statut: corps?.envoi?.statut === 'deja_recue' ? 'deja_recue' : 'acceptee', id: o.bon.id });
    await supprimer([o.clientOpId]); bilan.envoyees++;
    return;
  }
  if (r.status >= 400 && r.status < 500) {
    await ecrire([refus(o, corps?.erreur?.message ?? `Enregistrement refusé (${r.status}).`, r.status, corps?.erreur?.code)]); bilan.refusees++;
    return;
  }
  await noterEssai([o]);
  throw new Arret('serveur');
}

async function synchroniserVraiment(proprio: string): Promise<Bilan> {
  const bilan: Bilan = { envoyees: 0, refusees: 0, arret: null, avertissements: [] };
  if (!navigator.onLine) { bilan.arret = 'reseau'; return bilan; }
  try {
    const toutes = (await lister(proprio)).filter((o) => o.etat === 'en_attente');
    const ops = toutes.filter((o) => o.type !== 'bon');
    // Bons d'intervention d'abord, un enregistrement à la fois, dans l'ordre de saisie (le suivant d'un même bon s'appuie sur le précédent).
    for (const o of toutes.filter((x) => x.type === 'bon')) await envoyerBon(o, bilan);
    const parChantier = new Map<string, OperationFile[]>();
    for (const o of ops) parChantier.set(o.chantierId, [...(parChantier.get(o.chantierId) ?? []), o]);
    for (const [chantierId, liste] of parChantier) {
      let lot: OperationFile[] = [];
      const differees: OperationFile[] = [];
      const vider = async () => { if (lot.length) { const l = lot; lot = []; await envoyerLot(chantierId, l, bilan); } };
      for (const o of liste) {
        if (o.type === 'photo') {
          if (o.prealable) { await vider(); await envoyerPhoto(o, bilan); } else differees.push(o);
          continue;
        }
        lot.push(o);
        if (lot.length >= TAILLE_LOT) await vider();
      }
      await vider();
      // Photos ensuite, une par une : les coches et consignes arrivent même si la photo est longue à partir.
      for (const p of differees) await envoyerPhoto(p, bilan);
    }
  } catch (e) {
    if (e instanceof Arret) bilan.arret = e.raison; else throw e;
  }
  return bilan;
}

let enCours: Promise<Bilan> | null = null;
export const envoiEnCours = () => enCours !== null;

/** Envoie la file de ce compte (un seul envoi à la fois, même entre onglets). */
export function synchroniser(proprio: string): Promise<Bilan> {
  if (enCours) return enCours;
  const lancer = () => synchroniserVraiment(proprio);
  const verrous = (navigator as Navigator & { locks?: LockManager }).locks;
  enCours = (verrous?.request ? verrous.request(`trame-envois:${proprio}`, lancer) as Promise<Bilan> : lancer())
    .finally(() => { enCours = null; signaler(); });
  return enCours;
}

export interface Soumission {
  /** envoyee : arrivée sur le serveur ; en_attente : gardée sur le téléphone, partira au retour du réseau ; refusee : à corriger. */
  etat: 'envoyee' | 'en_attente' | 'refusee';
  verdict?: Verdict;
  message?: string;
  /** Saisies secondaires (photo jointe…) refusées alors que la principale est passée : elles restent dans « Envois refusés ». */
  secondairesRefusees: OperationFile[];
  avertissements: string[];
}

/**
 * Saisie depuis un volet : rangée d'abord dans la file (durable), puis envoyée tout de suite s'il y a du réseau.
 * En ligne, un refus de la saisie PRINCIPALE la retire de la file et revient au volet (encore ouvert) pour être corrigée ; sans
 * réseau, ou si le réseau lâche pendant l'envoi, elle reste dans la file et partira seule.
 */
export async function soumettre(proprio: string, nouvelles: NouvelleOperation[], principale = nouvelles.length - 1): Promise<Soumission> {
  const ops = await ajouter(proprio, nouvelles);
  const ids = ops.map((o) => o.clientOpId);
  const idPrincipal = ids[principale];
  const restantes = async () => (await lister(proprio)).filter((o) => ids.includes(o.clientOpId));
  if (!navigator.onLine) return { etat: 'en_attente', secondairesRefusees: [], avertissements: [] };
  const avertissements: string[] = [];
  for (let essai = 0; essai < 2; essai++) {
    const b = await synchroniser(proprio);
    avertissements.push(...b.avertissements);
    const r = await restantes();
    if (b.arret || !r.some((o) => o.etat === 'en_attente')) break;
  }
  const r = await restantes();
  const principal = r.find((o) => o.clientOpId === idPrincipal);
  if (principal?.etat === 'refusee') {
    await supprimer(r.filter((o) => o.etat === 'refusee').map((o) => o.clientOpId));
    return { etat: 'refusee', message: principal.refus?.message, secondairesRefusees: [], avertissements };
  }
  const secondairesRefusees = r.filter((o) => o.etat === 'refusee');
  if (principal) return { etat: 'en_attente', secondairesRefusees, avertissements };
  return { etat: 'envoyee', verdict: verdictDe(idPrincipal), secondairesRefusees, avertissements: verdictDe(idPrincipal)?.avertissements ?? [] };
}

// ═══ Lecture pour l'interface ═══════════════════════════════════════════════════════════════════════════
/** La file de ce compte, tenue à jour (ajout, envoi, refus, autre onglet). */
export function useFile(proprio: string) {
  const [ops, setOps] = useState<OperationFile[]>([]);
  const [lue, setLue] = useState(false);
  useEffect(() => {
    let vivant = true;
    const maj = () => { lister(proprio).then((l) => { if (vivant) { setOps(l); setLue(true); } }).catch(() => { if (vivant) setLue(true); }); };
    maj();
    window.addEventListener(EVT, maj);
    canal?.addEventListener('message', maj);
    return () => { vivant = false; window.removeEventListener(EVT, maj); canal?.removeEventListener('message', maj); };
  }, [proprio]);
  return { ops, lue, enAttente: ops.filter((o) => o.etat === 'en_attente'), refusees: ops.filter((o) => o.etat === 'refusee') };
}

/** « 3 saisies », « 1 saisie ». */
export const saisies = (n: number) => `${n} saisie${n > 1 ? 's' : ''}`;

/** La file du compte connecté. */
export function useFileEnvoi() {
  const { moi } = useSession();
  const proprio = proprioDe(moi);
  return { proprio, ...useFile(proprio) };
}
